/**
 * Row of "contact the client" buttons (Telegram / Instagram / Viber / call).
 * Each button appears only when the matching data exists. Shared by the
 * reminder cards and the client profile.
 */
import { CSSProperties } from "react";
import { openChannelLink } from "../telegram.js";

export interface ContactInfo {
  phone?: string | null;
  instagram?: string | null;
  telegramUsername?: string | null;
  viberPhone?: string | null;
  /** false = background check confirmed the handle is dead → hide the button. */
  instagramActive?: boolean | null;
}

interface ChannelBtn {
  label: string;
  url: string;
  icon: string;
  color: string;
}

/**
 * Returns a clean Instagram handle, or null if the stored value isn't a
 * usable one. Some Bumpix records hold free text in this field (e.g.
 * "через ніку", "через телефон") — those must not produce a broken button.
 */
function cleanInstagramHandle(raw: string): string | null {
  const h = raw
    .trim()
    .replace(/^@+/, "")
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "")
    .replace(/[/?#].*$/, "")
    .trim();
  return /^[A-Za-z0-9._]{1,30}$/.test(h) ? h : null;
}

function buildChannels(c: ContactInfo): ChannelBtn[] {
  const out: ChannelBtn[] = [];
  // Telegram is only reliable when we have a real @username. The "find by
  // phone" fallback (t.me/+<phone>) needed the contact to allow "знайти
  // за номером" in their TG privacy settings AND for our clicker to have
  // them saved — almost never the case, so it kept showing "користувача
  // немає". Hide the button entirely when there's no @username.
  if (c.telegramUsername) {
    out.push({
      label: "Telegram",
      icon: "✈️",
      url: `https://t.me/${c.telegramUsername.replace(/^@/, "")}`,
      color: "#229ED9",
    });
  }
  // Show Instagram only if the stored value is a real handle AND the
  // background check hasn't confirmed it dead.
  const igHandle = c.instagram ? cleanInstagramHandle(c.instagram) : null;
  if (igHandle && c.instagramActive !== false) {
    out.push({
      label: "Instagram",
      icon: "📷",
      url: `https://www.instagram.com/${igHandle}/`,
      color: "#C13584",
    });
  }
  if (c.viberPhone || c.phone) {
    const num = (c.viberPhone || c.phone || "").replace(/[^\d]/g, "");
    out.push({
      label: "Viber",
      icon: "💬",
      url: `viber://chat?number=%2B${num}`,
      color: "#665CAC",
    });
  }
  if (c.phone) {
    out.push({
      label: "Дзвонити",
      icon: "📞",
      url: `tel:${c.phone}`,
      color: "#34c759",
    });
  }
  return out;
}

export function ContactButtons({ client }: { client: ContactInfo }) {
  const channels = buildChannels(client);
  if (channels.length === 0) return null;
  return (
    <div
      style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0" }}
    >
      {channels.map((c) => {
        // Telegram links go through openTelegramLink so the Mini App stays
        // open inside the same chat. Everything else (including viber:// and
        // tel:) we render as a plain anchor — Telegram WebApp's JS-triggered
        // openLink silently drops custom URI schemes on iOS, but native
        // anchor clicks are handed off to the OS which dispatches them.
        const isTg =
          c.url.startsWith("https://t.me/") || c.url.startsWith("tg://");
        return (
          <a
            key={c.label}
            href={c.url}
            target={isTg ? undefined : "_blank"}
            rel="noopener"
            onClick={
              isTg
                ? (e) => {
                    e.preventDefault();
                    openChannelLink(c.url);
                  }
                : undefined
            }
            style={chBtn(c.color)}
          >
            {c.icon} {c.label}
          </a>
        );
      })}
    </div>
  );
}

function chBtn(bg: string): CSSProperties {
  return {
    padding: "8px 12px",
    background: bg,
    color: "white",
    border: 0,
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-block",
  };
}
